import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useWatchAuth } from '@/hooks/useWatchAuth'
import Layout from '@/components/Layout'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import Users from '@/pages/Users'
import Streams from '@/pages/Streams'
import Categories from '@/pages/Categories'
import Bouquets from '@/pages/Bouquets'
import Series from '@/pages/Series'
import EPG from '@/pages/EPG'
import Transcoding from '@/pages/Transcoding'
import Settings from '@/pages/Settings'
import SportsEPG from '@/pages/SportsEPG'
import Import from '@/pages/Import'
import PlutoTV from '@/pages/PlutoTV'
import Tubi from '@/pages/Tubi'
import LiveEvents from '@/pages/LiveEvents'
import Downloads from '@/pages/Downloads'
import InstallGuide from '@/pages/InstallGuide'
import Analytics from '@/pages/Analytics'
import Watch from '@/pages/Watch'
import WatchLogin from '@/pages/WatchLogin'

const isWatchDomain = window.location.hostname.startsWith('watch.')

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function WatchProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, checkAuth } = useWatchAuth()

  if (isLoading) {
    checkAuth()
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  // Watch subdomain: serve player at / and login at /login (no /watch prefix)
  if (isWatchDomain) {
    return (
      <Routes>
        <Route path="/login" element={<WatchLogin />} />
        <Route path="/" element={
          <WatchProtectedRoute>
            <Watch />
          </WatchProtectedRoute>
        } />
        {/* Redirect old /watch URLs to root */}
        <Route path="/watch/login" element={<Navigate to="/login" replace />} />
        <Route path="/watch" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // Admin domain: normal admin routes
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/watch/login" element={<WatchLogin />} />
      <Route path="/watch" element={
        <WatchProtectedRoute>
          <Watch />
        </WatchProtectedRoute>
      } />
      <Route path="/install-guide" element={<InstallGuide />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="streams" element={<Streams />} />
        <Route path="categories" element={<Categories />} />
        <Route path="bouquets" element={<Bouquets />} />
        <Route path="series" element={<Series />} />
        <Route path="epg" element={<EPG />} />
        <Route path="sports-epg" element={<SportsEPG />} />
        <Route path="import" element={<Import />} />
        <Route path="plutotv" element={<PlutoTV />} />
        <Route path="tubi" element={<Tubi />} />
        <Route path="live-events" element={<LiveEvents />} />
        <Route path="transcoding" element={<Transcoding />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  )
}
